import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { PROD } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductsPage() {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [active, setActive] = useState("all");

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data || [])).catch(() => {});
    axios.get(`${API}/collections`).then((r) => setCollections(r.data || [])).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (active === "all" ? products : products.filter((p) => p.collection_slug === active)),
    [products, active]
  );

  return (
    <div className="pt-28 md:pt-36 pb-20 bg-white">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-16">
          <div className="md:col-span-5">
            <div className="label-eyebrow mb-3">{t.products.eyebrow}</div>
            <h1 className="font-display font-medium text-slate-900 text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight">{t.products.title}</h1>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-slate-600 text-lg leading-relaxed">{t.products.sub}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-10 pb-6 border-b border-slate-200" data-testid={PROD.filter}>
          <button
            data-testid="filter-all"
            onClick={() => setActive("all")}
            className={`px-4 py-2 text-[11px] font-grotesk font-semibold uppercase tracking-[0.18em] rounded-full border transition-colors ${
              active === "all" ? "bg-[var(--steel)] text-white border-[var(--steel)]" : "border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-400"
            }`}
          >{t.products.all}</button>
          {collections.map((c) => (
            <button
              key={c.slug}
              data-testid={`filter-${c.slug}`}
              onClick={() => setActive(c.slug)}
              className={`px-4 py-2 text-[11px] font-grotesk font-semibold uppercase tracking-[0.18em] rounded-full border transition-colors ${
                active === c.slug ? "bg-[var(--steel)] text-white border-[var(--steel)]" : "border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-400"
              }`}
            >{c.title}</button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.article
                key={p.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.5, delay: i * 0.03 }}
                data-testid={PROD.card}
                className="card-hover group rounded-xl overflow-hidden bg-white border border-slate-200"
              >
                <div className="relative aspect-[4/5] bg-slate-100 overflow-hidden">
                  {p.image ? (
                    <img src={p.image} alt="" loading="lazy" className="pc-img absolute inset-0 w-full h-full object-cover" />
                  ) : (
                    <div className="absolute inset-0 grid place-items-center bg-gradient-to-br from-[var(--steel-soft)] to-slate-100">
                      <span className="label-eyebrow text-slate-400">Photo coming soon</span>
                    </div>
                  )}
                </div>
                <div className="p-5 md:p-6">
                  <div className="label-eyebrow text-slate-500 mb-2">{p.category}</div>
                  <h3 className="font-display text-slate-900 text-lg md:text-xl leading-tight tracking-tight mb-2">{p.title}</h3>
                  <p className="text-slate-600 text-[13.5px] leading-relaxed line-clamp-2 mb-4">{p.description}</p>
                  <Link
                    to="/contact"
                    data-testid={PROD.enquire}
                    className="inline-flex items-center gap-1.5 text-[var(--steel)] hover:text-[var(--steel-hover)] text-[11px] font-grotesk font-semibold uppercase tracking-[0.2em] transition-colors"
                  >
                    {t.products.enquire} <ArrowUpRight size={12} />
                  </Link>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
